import * as Sentry from '@sentry/react'
import LogRocket from 'logrocket'
import posthog from 'posthog-js'
import {
  isDev,
  isLocalhost,
  isProd,
  LOG_ROCKET_APP_ID,
  POSTHOG_API_HOST,
  POSTHOG_API_KEY,
  POSTHOG_UI_HOST,
  PRIVATE_FIELD_CLASS,
  SENTRY_DSN,
} from './v2/configs/constants'
import { autocaptureUrlAllowlist } from './v2/configs/posthog-autocapture'
import { isEmbedMode } from './v2/shared/utils/embed-session'
import { isAutomatedSession } from './v2/utils/automation'
import { loadGtm } from './v2/utils/gtm'
import { loadHubspotTracking } from './v2/utils/hubspot'
import { isInternalSession } from './v2/utils/internal-user'
import { redactRequestBody, redactRequestHeaders } from './v2/utils/network-redaction'

const environment = process.env.REACT_APP_ENV

// Third-party trackers (PostHog with unmasked replay, HubSpot, GTM) never load in a partner's
// embed iframe. Sentry stays on: it is our own error tracking.
const isEmbed = isEmbedMode()

// PostHog: loaded directly here instead of via the GTM container. Gated to the
// DEPLOYED dev/prod apps, never a developer's machine: `bun start` runs with
// REACT_APP_ENV=DEV (same .env.development the deployed dev build uses), so
// isDev alone does NOT exclude localhost — we also require the shared
// isLocalhost check. Session replay is ON and UNMASKED on PROD for everyone except
// @plivo.com staff — all visible text and input values are recorded (see the
// session_recording block below); LogRocket skips the same sessions, see
// initLogRocket below. Assigning to window.posthog
// preserves every existing `window.posthog?.` call site.
//
// Autocapture is OFF by default and allowlisted onto a handful of product
// surfaces (`configs/posthog-autocapture.ts`); replay text masking is also OFF
// (replay records content). The only remaining redaction is the `ph-no-capture` class
// on payment-card entry (Stripe/Cashfree) in billing, kept for PCI-DSS — billing is
// not an autocapture surface either way. Everything else — including end-customer PII,
// which on the allowlisted logs / phone-numbers pages includes numbers and call UUIDs
// landing in `$el_text` and `$elements_chain` — is captured by design.
//
// Dev and prod use separate PostHog projects (POSTHOG_API_KEY comes from the env file).
// Replay and exception capture are prod-only.
if ((isDev || isProd) && !isLocalhost && !isEmbed) {
  try {
    posthog.init(POSTHOG_API_KEY, {
      api_host: POSTHOG_API_HOST,
      ui_host: POSTHOG_UI_HOST,
      defaults: '2026-01-30',
      capture_pageview: 'history_change',
      capture_pageleave: true,
      // Autocapture is OFF everywhere except an explicit allowlist of product surfaces
      // (home, AI agents, agent runs, SIP Trunking, applications, phone numbers, logs,
      // chat widget) — see `configs/posthog-autocapture.ts` for the list and for how
      // posthog-js matches these regexes (full href, not pathname). The list is
      // host-agnostic, so it applies to the deployed dev console as well as prod; a
      // developer's machine is excluded by the isLocalhost gate on this whole block.
      // Everywhere else only pageviews, pageleaves and explicit capture() calls are sent.
      //
      // NOTE this object alone is NOT sufficient: autocapture is also gated SERVER-side
      // by the project's `autocapture_opt_out`, and the server's opt-out WINS over the
      // client config (`Autocapture.isEnabled` returns `!disabledClient && !disabledServer`).
      // That is the opposite precedence to capture_performance below. The project toggle
      // must be ON for any of this to emit; verify with
      // `curl https://f.plivo.com/array/<token>/config | grep autocapture_opt_out`.
      //
      // DO NOT flip that toggle as a solo step. It is per-PROJECT, and the prod
      // POSTHOG_API_KEY is shared with `www.plivo.com` and `docs.plivo.com`, whose snippets pass no
      // `autocapture` key at all (verified against the live www.plivo.com HTML, 2026-08-14)
      // and so have no client-side opt-out to fall back on. Measured from the window before
      // the project opted out (≤ 2026-08-04), turning it on costs ~2.6-5.1k autocapture
      // events/day from www.plivo.com and ~1-35/day from docs.plivo.com — neither of which
      // this allowlist can constrain, because a url_allowlist only binds the client that
      // declares it. Sequence: get those two snippets to pin `autocapture: false` (or move
      // the console to its own project) FIRST, then flip. hodor shares the same key but is
      // server-side, and autocapture is browser-only, so it is unaffected.
      autocapture: {
        url_allowlist: autocaptureUrlAllowlist,
      },
      // Web vitals / network timing OFF. Pinned client-side rather than left to the
      // project's remote config: an explicit boolean wins over the server flag, so
      // this cannot be switched back on by a settings change or a stale cached
      // config. It was the single largest non-pageview event source (~13k/day).
      capture_performance: false,
      // Unhandled errors + rejections, prod only. Sentry is the error tracker for
      // both envs; this exists for PostHog's error tab. On dev it was mostly one
      // repeating ServiceWorker failure (~3.9k events/day).
      capture_exceptions: isProd,
      // Session replay ON and unmasked on prod: record all visible text and input
      // values. No maskTextSelector (PostHog default = no text masking) and
      // maskAllInputs:false so typed values are captured too. PostHog still always
      // masks <input type="password">. PRIVATE_FIELD_CLASS nodes stay blocked —
      // posthog-js blocks that class natively; LogRocket needs telling separately
      // below, and only doing one is how card fields leaked into LogRocket.
      //
      // Staff and E2E runs are dropped here rather than after identify: PostHog
      // bills per recording, and one that has flushed a snapshot is already
      // billable. Staff were 4.4% of prod recordings (SER-6151).
      disable_session_recording: !isProd || isInternalSession() || isAutomatedSession(),
      session_recording: {
        maskAllInputs: false,
      },
    })
    window.posthog = posthog
  } catch (error) {
    Sentry.captureException(error)
  }
}

// HubSpot tracking code. Injected here rather than as a tag in index.html so it can
// be gated (deployed dev/prod only, each on its own portal) and so the initial path
// is seeded before the script loads. SPA navigations are reported by <HubspotTracker />.
if (!isEmbed) {
  loadHubspotTracking()
}

// Google Tag Manager, pre-login page loads only. Same reasoning as HubSpot for why
// it is called here rather than inlined in index.html, plus one more: as a raw
// snippet it ran on every authenticated page, which made the container a way to put
// arbitrary JS next to the session token and the card forms without a PR.
// gtm.ts gates on "no console session", which is also true inside the embed iframe.
if (!isEmbed) {
  loadGtm()
}

// Deferred until authenticated — auto-loading on login/sign-up burned quota, and
// LogRocket bills per session. Staff and E2E are skipped via the same helpers
// PostHog uses, so "internal" has one definition. SER-6151 gated PostHog only and
// missed this. Cost: staff Sentry events lose their LogRocket sessionURL.
let logRocketInitialized = false
export const initLogRocket = () => {
  if (logRocketInitialized || !isProd || isInternalSession() || isAutomatedSession()) return
  try {
    LogRocket.init(LOG_ROCKET_APP_ID, {
      // PRIVATE_FIELD_CLASS is PostHog's convention and LogRocket knew nothing
      // about it, so card fields PostHog blocked were still recorded here. Naming
      // it makes one marker cover both. data-private on the inputs is the backup.
      dom: {
        privateClassNameBlocklist: [PRIVATE_FIELD_CLASS],
      },
      network: {
        // dom only covers replay snapshots; network is captured separately and
        // addCard posts the raw PAN/CVV in the body. The old sanitizer rewrote
        // only "password", so every add-card shipped the card number here.
        requestSanitizer: (request) => {
          try {
            // Deny-list by key name (see network-redaction): replay otherwise
            // captures provider OAuth client_secrets, custom-MCP bearer tokens,
            // integration api_keys and the card PAN billing posts, verbatim.
            request.headers = redactRequestHeaders(request.headers)
            request.body = redactRequestBody(request.body)
          } catch {
            // Drop BOTH if sanitization fails — safer than letting a
            // partially-sanitized payload reach LogRocket. Headers matter as much
            // as the body here: if redactRequestHeaders threw, request.headers is
            // still the untouched original carrying Authorization.
            request.headers = undefined
            request.body = undefined
          }
          return request
        },
        // Headers as well as body, mirroring the request side: a response can
        // carry a refreshed bearer token or a signed URL in a header, and the
        // "in case an endpoint echoes something back" reasoning applies there too.
        responseSanitizer: (response) => {
          try {
            response.headers = redactRequestHeaders(response.headers)
            response.body = redactRequestBody(response.body)
          } catch {
            response.headers = undefined
            response.body = undefined
          }
          return response
        },
      },
    })
    LogRocket.getSessionURL((sessionURL) => {
      Sentry.getCurrentScope().setExtra('sessionURL', sessionURL)
    })
    logRocketInitialized = true
  } catch (error) {
    Sentry.captureException(error)
  }
}

// Initialize Sentry only in dev and prod environments (not localhost)
if (isDev || isProd) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment,
    integrations: [Sentry.browserTracingIntegration()],
    tracePropagationTargets: ['contacto.com', 'contactodev.com', 'plivo.com', /^\//],

    // Environment-specific sampling rates (dev: 10%, prod: 100%)
    tracesSampleRate: isDev ? 0.1 : 1.0,

    // Enhanced error filtering
    ignoreErrors: [
      'Failed to fetch',
      'NetworkError',
      'Network request failed',
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'Script error',
      'Non-Error promise rejection captured',
      'Non-Error exception captured',
      /Region is not set, cannot make request/,
      /Loading chunk \d+ failed/,
      /ChunkLoadError/,
      'Response Interceptor - 401 Unauthorized - Logging out',
      /Request failed with status code 4\d\d/,
      // Environmental: the user's disk / browser storage quota is full, so an
      // IndexedDB (React Query cache persist) write fails. Nothing we can fix —
      // the app degrades to the in-memory cache — and it just adds noise. (SER-6018)
      'FILE_ERROR_NO_SPACE',
      'QuotaExceededError',
      'NS_ERROR_DOM_QUOTA_REACHED',
    ],

    denyUrls: [/extensions\//i, /^chrome:\/\//i, /^chrome-extension:\/\//i, /^moz-extension:\/\//i],

    beforeSend(event) {
      try {
        // Attach LogRocket session URL (prod only)
        if (isProd) {
          const logRocketSession = LogRocket?.sessionURL
          if (logRocketSession && event && typeof event === 'object') {
            if (!event.extra) return { ...event, extra: { LogRocket_timestamp: logRocketSession } }
            return { ...event, extra: { ...event.extra, LogRocket_timestamp: logRocketSession } }
          }
        }
        return event
      } catch {
        return event
      }
    },
  })
}

export { LogRocket, Sentry }
